import React, { useState, useEffect } from 'react';
import { formatDateTime } from '../../utils/dateUtils';
import { copyBackupCodes, downloadBackupCodes, printBackupCodes } from '../../utils/backupCodes';
import EmployeeDocuments from './EmployeeDocuments';
import { UserProfileService, IPasswordUpdate } from '../../services/user-profile.service';
import { IUserProfile, IUserProfileUpdate } from '../../interfaces/user-profile.interface';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Typography,
    Box,
    Alert,
    IconButton,
    TextField,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { ImpersonationGrantService } from '../../services/impersonation-grant.service';
import { TwoFactorService, ITwoFactorStatus, ITwoFactorSetup } from '../../services/two-factor.service';
import { displayUsername } from '../../utils/userUtils';
import { IImpersonationGrant } from '../../interfaces/impersonation-grant.interface';
import { validatePassword } from '../../utils/passwordPolicy';
import { useTranslation } from 'react-i18next';
import { useRoleLabel } from './useRoleLabel';

const DURATION_OPTIONS = [
    { labelKey: 'oneHour', value: 1 },
    { labelKey: 'fourHours', value: 4 },
    { labelKey: 'oneDay', value: 24 },
    { labelKey: 'sevenDays', value: 168 },
] as const;

// Profile-local enrollment confirm form. Distinct from `auth/TwoFactorEnroll`, which drives the
// LOGIN-time enrollment flow and completes via `useAuth().completeTwoFactorLogin`. This form is for
// an already-authenticated user enabling 2FA from their profile, so it completes via
// `TwoFactorService.enable` instead — never wire this to `completeTwoFactorLogin`.
const EnrollConfirmForm: React.FC<{ challengeId: string; onEnrolled: (codes: string[]) => void }> = ({ challengeId, onEnrolled }) => {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const { t } = useTranslation(['users', 'common']);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            const codes = await TwoFactorService.enable(challengeId, code);
            onEnrolled(codes);
        } catch (err: any) {
            setError(err?.response?.data || err?.message || t('userProfile.invalidCode'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
                label={t('userProfile.authenticationCode')}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                fullWidth
                required
                size="small"
                sx={{ mb: 2 }}
            />
            <Button type="submit" variant="contained" fullWidth disabled={submitting}>
                {submitting ? t('userProfile.confirming') : t('common:actions.confirm')}
            </Button>
        </form>
    );
};

const UserProfile = () => {
    const [profile, setProfile] = useState<IUserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const toast = useToast();
    const { user } = useAuth();
    const { t } = useTranslation(['users', 'common']);
    const roleLabel = useRoleLabel();
    const [isEditing, setIsEditing] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [formData, setFormData] = useState<IUserProfileUpdate>({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        bankName: '',
        bankAccountNumber: '',
        mobileBankingProvider: '',
        mobileBankingAccount: '',
    });
    const [passwordData, setPasswordData] = useState<IPasswordUpdate>({
        previousPassword: '',
        newPassword: '',
    });
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState({ previousPassword: false, newPassword: false, confirmPassword: false });
    const navigate = useNavigate();

    // Grant access state
    const [activeGrant, setActiveGrant] = useState<IImpersonationGrant | null>(null);
    const [grantDialogOpen, setGrantDialogOpen] = useState(false);
    const [selectedDuration, setSelectedDuration] = useState<number>(1);
    const [grantLoading, setGrantLoading] = useState(false);

    // Two-factor authentication (self-service) state
    const [twoFactorStatus, setTwoFactorStatus] = useState<ITwoFactorStatus | null>(null);
    const [twoFactorSetup, setTwoFactorSetup] = useState<ITwoFactorSetup | null>(null);
    const [enrolledBackupCodes, setEnrolledBackupCodes] = useState<string[] | null>(null);
    const [savedConfirmed, setSavedConfirmed] = useState(false);
    const [disableCode, setDisableCode] = useState('');
    const [isDisabling2fa, setIsDisabling2fa] = useState(false);

    const isSystemAdmin = user?.roles === 'SystemAdmin';

    const newPasswordErrors = validatePassword(passwordData.newPassword, profile?.username, profile?.phone);

    useEffect(() => {
        fetchUserProfile();
        if (!isSystemAdmin) {
            fetchActiveGrant();
        }
        TwoFactorService.getStatus().then(setTwoFactorStatus).catch(() => setTwoFactorStatus(null));
    }, []);

    const fetchUserProfile = async () => {
        setLoading(true);
        try {
            const userProfile = await UserProfileService.getLoggedInUserProfile();
            setProfile(userProfile);
            setFormData({
                email: userProfile.email || '',
                firstName: userProfile.firstName || '',
                lastName: userProfile.lastName || '',
                phone: userProfile.phone || '',
                bankName: userProfile.bankName || '',
                bankAccountNumber: userProfile.bankAccountNumber || '',
                mobileBankingProvider: userProfile.mobileBankingProvider || '',
                mobileBankingAccount: userProfile.mobileBankingAccount || '',
            });
        } catch (err) {
            console.error('Failed to fetch profile:', err);
            toast.showApiError(err, t('userProfile.toasts.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const fetchActiveGrant = async () => {
        try {
            const grant = await ImpersonationGrantService.getMyGrant();
            setActiveGrant(grant);
        } catch {
            setActiveGrant(null);
        }
    };

    const handleGrantAccess = async () => {
        setGrantLoading(true);
        try {
            const grant = await ImpersonationGrantService.createGrant(selectedDuration);
            setActiveGrant(grant);
            setGrantDialogOpen(false);
            toast.showSuccess(t('userProfile.toasts.grantSuccess'));
        } catch (err) {
            toast.showApiError(err, t('userProfile.toasts.grantFailed'));
        } finally {
            setGrantLoading(false);
        }
    };

    const handleRevokeGrant = async () => {
        setGrantLoading(true);
        try {
            await ImpersonationGrantService.revokeMyGrant();
            setActiveGrant(null);
            toast.showSuccess(t('userProfile.toasts.revoked'));
        } catch (err) {
            toast.showApiError(err, t('userProfile.toasts.revokeFailed'));
        } finally {
            setGrantLoading(false);
        }
    };

    const handleStartTwoFactorSetup = async () => {
        try {
            const setup = await TwoFactorService.setup();
            setTwoFactorSetup(setup);
        } catch (err) {
            toast.showApiError(err, t('userProfile.toasts.twoFactorSetupFailed'));
        }
    };

    const handleTwoFactorEnrolled = (backupCodes: string[]) => {
        setEnrolledBackupCodes(backupCodes);
    };

    const handleCloseEnrollDialog = async () => {
        setTwoFactorSetup(null);
        setEnrolledBackupCodes(null);
        setSavedConfirmed(false);
        const status = await TwoFactorService.getStatus();
        setTwoFactorStatus(status);
        toast.showSuccess(t('userProfile.toasts.twoFactorEnabled'));
    };

    const handleDisableTwoFactor = async () => {
        try {
            await TwoFactorService.disable(disableCode);
            setIsDisabling2fa(false);
            setDisableCode('');
            const status = await TwoFactorService.getStatus();
            setTwoFactorStatus(status);
            toast.showSuccess(t('userProfile.toasts.twoFactorDisabled'));
        } catch (err) {
            toast.showApiError(err, t('userProfile.toasts.twoFactorDisableFailed'));
        }
    };

    const handleRegenerateBackupCodes = async () => {
        const code = window.prompt(t('userProfile.regeneratePrompt'));
        if (!code) return;
        try {
            await TwoFactorService.regenerateBackupCodes(code);
            const status = await TwoFactorService.getStatus();
            setTwoFactorStatus(status);
            toast.showSuccess(t('userProfile.toasts.backupCodesRegenerated'));
        } catch (err) {
            toast.showApiError(err, t('userProfile.toasts.backupCodesRegenerateFailed'));
        }
    };

    // Profile-local enroll dialog (distinct from auth/TwoFactorEnroll, which drives the login-time flow).
    // Two-step: confirm the code via TwoFactorService.enable, then show the returned backup codes
    // before closing — mirrors the two-step UX in TwoFactorEnroll.tsx (Task 2).
    const renderEnrollDialog = () => {
        if (!twoFactorSetup) return null;
        return (
            <Dialog open onClose={() => { setTwoFactorSetup(null); setEnrolledBackupCodes(null); setSavedConfirmed(false); }}>
                <DialogTitle>{t('userProfile.setUpTwoFactor')}</DialogTitle>
                <DialogContent sx={{ minWidth: 320, pt: 2 }}>
                    {enrolledBackupCodes ? (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('userProfile.backupCodesHint')}
                            </Typography>
                            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2, fontFamily: 'monospace' }}>
                                {enrolledBackupCodes.map((c) => <div key={c}>{c}</div>)}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <Button size="small" onClick={() => copyBackupCodes(enrolledBackupCodes).then(() => toast.showSuccess(t('userProfile.toasts.backupCodesCopied')))}>{t('userProfile.copy')}</Button>
                                <Button size="small" onClick={() => downloadBackupCodes(enrolledBackupCodes)}>{t('common:actions.download')}</Button>
                                <Button size="small" onClick={() => printBackupCodes(enrolledBackupCodes)}>{t('common:actions.print')}</Button>
                            </Box>
                            <FormControlLabel
                                control={<Checkbox checked={savedConfirmed} onChange={(e) => setSavedConfirmed(e.target.checked)} />}
                                label={t('userProfile.savedBackupCodes')}
                                sx={{ mb: 2 }}
                            />
                            <Button
                                variant="contained"
                                fullWidth
                                disabled={!savedConfirmed}
                                onClick={handleCloseEnrollDialog}
                            >
                                {t('userProfile.continue')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Box textAlign="center" sx={{ mb: 2 }}>
                                <img src={twoFactorSetup.qrDataUri} alt={t('userProfile.qrAlt')} style={{ width: 200, height: 200 }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('userProfile.manualCode')} <strong>{twoFactorSetup.secret}</strong>
                            </Typography>
                            <EnrollConfirmForm challengeId={twoFactorSetup.challengeId} onEnrolled={handleTwoFactorEnrolled} />
                        </>
                    )}
                </DialogContent>
            </Dialog>
        );
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!profile) return;

        setLoading(true);
        try {
            const updatedProfile = await UserProfileService.updateUserProfile(
                profile.id,
                formData
            );
            setProfile(updatedProfile);
            setIsEditing(false);
        } catch (err) {
            console.error('Failed to update profile:', err);
            toast.showApiError(err, t('userProfile.toasts.updateFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'confirmPassword') {
            setConfirmPassword(value);
        } else {
            setPasswordData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (passwordData.newPassword !== confirmPassword) {
            toast.showError(t('userProfile.toasts.passwordMismatch'));
            return;
        }

        if (newPasswordErrors.length > 0) {
            toast.showError(t('userCreate.passwordTooWeak'));
            return;
        }

        setLoading(true);

        try {
            await UserProfileService.updatePassword(passwordData);
            toast.showSuccess(t('userProfile.toasts.passwordUpdated'));
            setIsChangingPassword(false);
            setPasswordData({ previousPassword: '', newPassword: '' });
            setConfirmPassword('');
        } catch (err: any) {
            toast.showApiError(err, t('userProfile.toasts.passwordUpdateFailed'));
        } finally {
            setLoading(false);
        }
    };

    if (loading && !profile) {
        return <div>{t('userProfile.loading')}</div>;
    }

    return (
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
            <h2>{t('userProfile.title')}</h2>

            {!isEditing && !isChangingPassword ? (
                <div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>{t('userProfile.fields.email')}</strong> {profile?.email}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>{t('userProfile.fields.firstName')}</strong> {profile?.firstName}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>{t('userProfile.fields.lastName')}</strong> {profile?.lastName}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>{t('userProfile.fields.phone')}</strong> {profile?.phone}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>{t('userProfile.fields.username')}</strong> {displayUsername(profile?.username)}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>{t('userProfile.fields.type')}</strong> {roleLabel(profile?.type)}
                    </div>
                    <div style={{ marginTop: '20px', marginBottom: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
                        <h3 style={{ margin: '0 0 10px 0' }}>{t('userProfile.banking')}</h3>
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>{t('userProfile.fields.bankName')}</strong> {profile?.bankName || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>{t('userProfile.fields.bankAccountNumber')}</strong> {profile?.bankAccountNumber || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>{t('userProfile.fields.mobileBankingProvider')}</strong> {profile?.mobileBankingProvider || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>{t('userProfile.fields.mobileBankingAccount')}</strong> {profile?.mobileBankingAccount || '-'}
                    </div>
                    <button
                        onClick={() => setIsEditing(true)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#006a4e',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                        }}
                    >
                        {t('userProfile.editProfile')}
                    </button>
                    <button
                        onClick={() => setIsChangingPassword(true)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#ffc107',
                            color: 'black',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                        }}
                    >
                        {t('userProfile.changePassword')}
                    </button>
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        {t('userProfile.back')}
                    </button>

                    {/* Grant Access Section - all non-SystemAdmin users */}
                    {!isSystemAdmin && (
                        <Box sx={{ mt: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            <Typography variant="h6" gutterBottom>{t('userProfile.adminAccessGrant')}</Typography>
                            {activeGrant ? (
                                <>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        {t('userProfile.activeGrant', { expiresAt: formatDateTime(activeGrant.expiresAt) })}
                                    </Alert>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleRevokeGrant}
                                        disabled={grantLoading}
                                    >
                                        {t('userProfile.revokeAccess')}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {t('userProfile.grantHint')}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        onClick={() => setGrantDialogOpen(true)}
                                        disabled={grantLoading}
                                    >
                                        {t('userProfile.grantAdminAccess')}
                                    </Button>
                                </>
                            )}
                        </Box>
                    )}

                    <Box sx={{ mt: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>{t('userProfile.twoFactor')}</Typography>
                        {twoFactorStatus?.enabled ? (
                            <>
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    {twoFactorStatus.backupCodesDisabled ? t('userProfile.enabledBackupBlocked') : t('userProfile.enabled')}
                                </Alert>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {t('userProfile.backupCodesRemaining', { remaining: twoFactorStatus.backupCodesRemaining })}
                                </Typography>
                                {isDisabling2fa ? (
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <TextField
                                            label={t('userProfile.currentCode')}
                                            size="small"
                                            value={disableCode}
                                            onChange={(e) => setDisableCode(e.target.value)}
                                        />
                                        <Button variant="contained" color="error" onClick={handleDisableTwoFactor}>
                                            {t('userProfile.confirmDisable')}
                                        </Button>
                                        <Button variant="outlined" onClick={() => { setIsDisabling2fa(false); setDisableCode(''); }}>
                                            {t('common:actions.cancel')}
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button variant="outlined" color="error" onClick={() => setIsDisabling2fa(true)}>
                                            {t('userProfile.disableTwoFactor')}
                                        </Button>
                                        {!twoFactorStatus.backupCodesDisabled && (
                                            <Button variant="outlined" onClick={handleRegenerateBackupCodes}>
                                                {t('userProfile.regenerateBackupCodes')}
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </>
                        ) : (
                            <>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {t('userProfile.twoFactorHint')}
                                </Typography>
                                <Button variant="contained" onClick={handleStartTwoFactorSetup}>
                                    {t('userProfile.enableTwoFactor')}
                                </Button>
                            </>
                        )}
                    </Box>

                    <EmployeeDocuments />
                </div>
            ) : isChangingPassword ? (
                <form onSubmit={handlePasswordSubmit}>
                    <h3>{t('userProfile.changePassword')}</h3>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            {t('userProfile.fields.currentPassword')}
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPasswords.previousPassword ? 'text' : 'password'}
                                name="previousPassword"
                                value={passwordData.previousPassword}
                                onChange={handlePasswordChange}
                                style={{ width: '100%', padding: '8px', paddingRight: '40px', boxSizing: 'border-box' }}
                                required
                            />
                            <IconButton
                                type="button"
                                onClick={() => setShowPasswords(s => ({ ...s, previousPassword: !s.previousPassword }))}
                                size="small"
                                aria-label={showPasswords.previousPassword ? t('userCreate.hidePassword') : t('userCreate.showPassword')}
                                style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)' }}
                            >
                                {showPasswords.previousPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </div>
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            {t('userProfile.fields.newPassword')}
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPasswords.newPassword ? 'text' : 'password'}
                                name="newPassword"
                                value={passwordData.newPassword}
                                onChange={handlePasswordChange}
                                style={{ width: '100%', padding: '8px', paddingRight: '40px', boxSizing: 'border-box' }}
                                required
                                minLength={3}
                            />
                            <IconButton
                                type="button"
                                onClick={() => setShowPasswords(s => ({ ...s, newPassword: !s.newPassword }))}
                                size="small"
                                aria-label={showPasswords.newPassword ? t('userCreate.hidePassword') : t('userCreate.showPassword')}
                                style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)' }}
                            >
                                {showPasswords.newPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </div>
                        {passwordData.newPassword.length > 0 && newPasswordErrors.length > 0 && (
                            <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px' }}>
                                {newPasswordErrors.join('; ')}
                            </div>
                        )}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            {t('userProfile.fields.confirmNewPassword')}
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPasswords.confirmPassword ? 'text' : 'password'}
                                name="confirmPassword"
                                value={confirmPassword}
                                onChange={handlePasswordChange}
                                style={{ width: '100%', padding: '8px', paddingRight: '40px', boxSizing: 'border-box' }}
                                required
                                minLength={3}
                            />
                            <IconButton
                                type="button"
                                onClick={() => setShowPasswords(s => ({ ...s, confirmPassword: !s.confirmPassword }))}
                                size="small"
                                aria-label={showPasswords.confirmPassword ? t('userCreate.hidePassword') : t('userCreate.showPassword')}
                                style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)' }}
                            >
                                {showPasswords.confirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={loading || newPasswordErrors.length > 0}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                        }}
                    >
                        {loading ? t('userProfile.updating') : t('userProfile.updatePassword')}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setIsChangingPassword(false);
                            setPasswordData({ previousPassword: '', newPassword: '' });
                            setConfirmPassword('');
                        }}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        {t('common:actions.cancel')}
                    </button>
                </form>
            ) : (
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            {t('userProfile.fields.email')}
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                            required
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            {t('userProfile.fields.firstName')}
                        </label>
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                            required
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            {t('userProfile.fields.lastName')}
                        </label>
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                            required
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            {t('userProfile.fields.phone')}
                        </label>
                        <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginTop: '20px', marginBottom: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
                        <h3 style={{ margin: '0 0 10px 0' }}>{t('userProfile.banking')}</h3>
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            {t('userProfile.fields.bankName')}
                        </label>
                        <input
                            type="text"
                            name="bankName"
                            value={formData.bankName || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            {t('userProfile.fields.bankAccountNumber')}
                        </label>
                        <input
                            type="text"
                            name="bankAccountNumber"
                            value={formData.bankAccountNumber || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            {t('userProfile.fields.mobileBankingProvider')}
                        </label>
                        <input
                            type="text"
                            name="mobileBankingProvider"
                            value={formData.mobileBankingProvider || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            {t('userProfile.fields.mobileBankingAccount')}
                        </label>
                        <input
                            type="text"
                            name="mobileBankingAccount"
                            value={formData.mobileBankingAccount || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>{t('userProfile.fields.username')}</strong> {displayUsername(profile?.username)}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>{t('userProfile.fields.type')}</strong> {roleLabel(profile?.type)}
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                        }}
                    >
                        {loading ? t('userProfile.saving') : t('manage.saveChanges')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        {t('common:actions.cancel')}
                    </button>
                </form>
            )}

            {/* Grant Duration Dialog */}
            <Dialog open={grantDialogOpen} onClose={() => setGrantDialogOpen(false)}>
                <DialogTitle>{t('userProfile.grantAdminAccess')}</DialogTitle>
                <DialogContent sx={{ minWidth: 320, pt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('userProfile.grantDurationHint')}
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel>{t('userProfile.duration')}</InputLabel>
                        <Select
                            value={selectedDuration}
                            label={t('userProfile.duration')}
                            onChange={(e) => setSelectedDuration(Number(e.target.value))}
                        >
                            {DURATION_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{t(`userProfile.durations.${opt.labelKey}`)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGrantDialogOpen(false)}>{t('common:actions.cancel')}</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleGrantAccess}
                        disabled={grantLoading}
                    >
                        {t('userProfile.grantAccess')}
                    </Button>
                </DialogActions>
            </Dialog>

            {renderEnrollDialog()}
        </div>
    );
};

export default UserProfile;
